import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RotateCcw, SlidersHorizontal, Wifi } from "lucide-react";
import { CANTONS } from "@/lib/subjects";
import { useAppTheme } from "@/lib/AppThemeContext";

const DEFAULTS = { minAge: 16, maxAge: 99, gender: "Alle", distance: 100, canton: "Alle", online: false };
const GENDERS = ["Alle", "weiblich", "männlich", "divers"];

export default function LeuteFilterModal({ open, onOpenChange, filters, onApply }) {
  const { theme } = useAppTheme();
  const [local, setLocal] = useState(filters);
  useEffect(() => setLocal(filters), [filters, open]);

  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const apply = () => { onApply(local); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`lf ${theme === "dark" ? "is-dark" : ""}`} data-testid="people-filter-dialog">
        <div className="lf-head">
          <span className="lf-icon"><SlidersHorizontal className="h-4 w-4" /></span>
          <div><DialogTitle className="lf-title">Filter</DialogTitle><DialogDescription className="lf-sub">Finde Lernende, die zu dir passen.</DialogDescription></div>
        </div>

        <div className="lf-body">
          <div className="lf-group">
            <div className="lf-label"><span>Alter</span><strong data-testid="people-filter-age-value">{local.minAge}–{local.maxAge} Jahre</strong></div>
            <input type="range" min="16" max="99" value={local.minAge} onChange={(e) => set("minAge", Math.min(Number(e.target.value), local.maxAge))} className="lf-range" aria-label="Mindestalter" data-testid="people-filter-min-age" />
            <input type="range" min="16" max="99" value={local.maxAge} onChange={(e) => set("maxAge", Math.max(Number(e.target.value), local.minAge))} className="lf-range" aria-label="Höchstalter" data-testid="people-filter-max-age" />
          </div>

          <div className="lf-group">
            <div className="lf-label"><span>Geschlecht</span></div>
            <div className="lf-chips" role="radiogroup" aria-label="Geschlecht">
              {GENDERS.map((g) => (
                <button key={g} type="button" role="radio" aria-checked={local.gender === g} onClick={() => set("gender", g)} className={`lf-chip ${local.gender === g ? "is-on" : ""}`} data-testid={`people-filter-gender-${g}`}>{g}</button>
              ))}
            </div>
          </div>

          <div className="lf-group">
            <div className="lf-label"><span>Distanz</span><strong data-testid="people-filter-distance-value">bis {local.distance} km</strong></div>
            <input type="range" min="1" max="250" value={local.distance} onChange={(e) => set("distance", Number(e.target.value))} className="lf-range" aria-label="Distanz" data-testid="people-filter-distance" />
          </div>

          <label className="lf-group">
            <div className="lf-label"><span>Kanton</span></div>
            <select value={local.canton} onChange={(e) => set("canton", e.target.value)} className="lf-select" data-testid="people-filter-canton">
              <option value="Alle">Alle Kantone</option>
              {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>

          <div className="lf-toggle-row">
            <span className="lf-toggle-copy"><Wifi className="h-4 w-4" /> Nur jetzt online</span>
            <button type="button" role="switch" aria-checked={local.online} onClick={() => set("online", !local.online)} className={`lf-switch ${local.online ? "is-on" : ""}`} data-testid="people-filter-online"><span /></button>
          </div>
        </div>

        <div className="lf-foot">
          <button type="button" onClick={() => setLocal(DEFAULTS)} className="lf-btn lf-btn-ghost" data-testid="people-filter-reset"><RotateCcw className="h-4 w-4" /> Zurücksetzen</button>
          <button type="button" onClick={apply} className="lf-btn" data-testid="people-filter-apply">Anwenden</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
